// Angular
import { Component, OnDestroy, OnInit } from '@angular/core';
// Services
import { CreateOrderService } from '../../service/create-order.service';
import { ImportOrderService } from './../../../import-order/service/import-order.service';
// Model
import { CREATE_MANUAL_ORDER, CreateOrderFormModel } from '../../model/create-order-form.model';
import { EngagementFileChange } from '../../../shared/engagement-file-upload/model/engagement-file-upload.model';
import { ProductModel } from '../../../metrics-dasboard/model/products.model';
// Other
import { debounceTime, Observable, Subject, takeUntil } from 'rxjs';
import { AppService, BroadcastService, CONTENT_LOADER_EVENT, ToasterType } from '@cv-invision/core';

@Component({
	selector: 'cv-create-order-form',
	templateUrl: './create-order-form.component.html',
	styleUrls: ['./create-order-form.component.scss']
})

export class CreateOrderFormComponent implements OnInit, OnDestroy {
	private organizationFilterSubject = new Subject<string>();
	private atComponentDestroy = new Subject<void>();
	public createOrderForm: CreateOrderFormModel = {
		form: '',
		product: '',
		schema: '',
		organization: '',
		referenceId: '',
		file: null,
		engagementFile: null
	};

	public formList$: Observable<any[]>;
	public productList$: Observable<ProductModel[]>;
	public organizationList$: Observable<any[]>;
	public filesDropMessage = 'Drag and drop your XML file here, or click to';
	public uploadLabelText = 'Upload Mismo XML File';
	public mismoFileErrorMessage: string | null = null;
	public referenceIdErrorMessage: string | null = null;
	public isEngagementFileValid = true;
	public fileDropped = false;
	public selectedForm: any = null;
	public selectedProduct;
	public displayDragAndDrop = true;
	public genericPDFForm: string; 
	constructor(
		private createOrderService: CreateOrderService,
		private broadcastService: BroadcastService,
		private importOrderService: ImportOrderService,
		private appService: AppService
	) { }

	/**
	 * Load available forms and organizations for dropdowns
	 */
	ngOnInit(): void {
		this.formList$ = this.importOrderService.getForms();
		this.productList$ = this.importOrderService.getProductsByMultipleTrackings();
		this.organizationList$ = this.createOrderService.getOrganizations();
		this.genericPDFForm = this.appService.getSchemaConfig().SCHEMA_CONFIG?.PDF_SCHEMA?.formType;
		this.organizationFilterSubject.pipe(
			debounceTime(500),
			takeUntil(this.atComponentDestroy)
		).subscribe((value: string) => {
			this.loadOrganizationOptions(value);
		});
	}

	/**
	 * Updates form and schema values when a form is selected from dropdown
	 * @param selected The selected form object containing form and schema
	 */
	public onFormChange(selected: any): void {
		this.createOrderForm.form = selected.form;
		this.createOrderForm.schema = selected.schema;
		this.selectedForm = selected;

		this.resetFileUpload();
		// Update message and label text based on schema
		if (selected.schema?.includes('3.6GSE')) {
			this.filesDropMessage = 'Drag and drop your ZIP file here, or click to';
			this.uploadLabelText = 'Upload Mismo ZIP File';
		} else if (selected.form === this.genericPDFForm) {
			this.filesDropMessage = 'Drag and drop your PDF file here, or click to';
			this.uploadLabelText = 'Upload Mismo PDF File';
		} else {
			this.filesDropMessage = 'Drag and drop your XML file here, or click to';
			this.uploadLabelText = 'Upload Mismo XML File';
		}
	}

	/**
	 * Captures the selected product on the form.
	 */
	public onProductChange(): void {
		this.createOrderForm.product = this.selectedProduct?.id;
	}

	/**
	 * Updates reference ID when input changes
	 * @param value The input value for externalId
	 */
	public onReferenceIdChange(value: string): void {
		this.createOrderForm.referenceId = value;
	}

	/**
	 * Validates the Reference ID field based on the following rules:
	 * - Must not exceed 30 characters in length.
	 * - Must not contain any whitespace characters (spaces, tabs, etc).
	 *
	 * If the value is invalid, sets an appropriate error message to `referenceIdErrorMessage`.
	 * If the value is valid, clears the error message.
	 */
	public validateReferenceId(): void {
		const value = this.createOrderForm.referenceId ?? '';
		if (value.length > 30) {
			this.referenceIdErrorMessage = 'Reference ID must be 30 characters or fewer.';
		} else if (/\s/.test(value)) {
			this.referenceIdErrorMessage = 'Reference ID cannot contain spaces.';
		} else {
			this.referenceIdErrorMessage = null;
		}
	}

	/**
	 * Handles dropped XML files, checks validity and updates state
	 * @param files List of dropped files
	 */
	public onFilesDropped(files: File[]): void {
		if (files.length > 1) {
			files.shift();
		}

		const selectedFile = files[0];
		const schema = this.createOrderForm?.schema ?? '';
		const isZip = selectedFile.name.toLowerCase().endsWith('.zip');
		const isXML =
			selectedFile.type === 'application/xml' ||
			selectedFile.type === 'text/xml' ||
			selectedFile.name.toLowerCase().endsWith('.xml');
		
		const isPDF =
		selectedFile.type === 'application/pdf' ||
		selectedFile.name.toLowerCase().endsWith('.pdf');

		this.createOrderForm.file = selectedFile;
		this.fileDropped = !!files.length;
	  
		const is36GSE = schema.includes('3.6GSE');
		const isPDFSchema = schema.includes('PDF');
		// Main validation
		if ((is36GSE && isZip) || (!is36GSE && isXML) && !isPDFSchema) {
			this.mismoFileErrorMessage = null;
		
		} else if (isPDFSchema) {
			if (isPDF) {
			this.mismoFileErrorMessage = null;
			} else {
				this.mismoFileErrorMessage = 'Only PDF files can be uploaded. Please try again.';
			}
		} else {
			this.mismoFileErrorMessage = is36GSE
			? 'Only ZIP files can be uploaded. Please try again.'
			: 'Only XML files can be uploaded. Please try again.';
		}
	  }

	/**
	 * Validates Generic PDF Form flow. 
	 * A product must be selected when using Generic PDF form uploads.
	 * @returns {boolean}
	 */
	isPDFFormInvalid() {
		const isPDFFormInvalid = !this.createOrderForm.product && this.createOrderForm.form === this.genericPDFForm;
		return isPDFFormInvalid;
	}

	/**
	 * Validates form and submits QC Order creation request
	 */
	public onSubmit(): void {
		const { form, schema, organization, referenceId, file, product, engagementFile } = this.createOrderForm;
		const isGeneralFormInvalid = !form || !schema || !organization || !referenceId || !(file instanceof File);
		if (isGeneralFormInvalid || this.isPDFFormInvalid()) {
			this.broadcastService.broadcast({
				name: ToasterType.ERROR,
				event: { error: 'Please complete all required fields before submitting.' },
			});
			return;
		}
		const formData = new FormData();
		formData.append('form', form);
		formData.append('schema', schema);
		formData.append('organizationId', organization);
		formData.append('externalId', referenceId);
		formData.append('file', file);
		if(!!this.isPDFFormInvalid && product) {
			formData.append('productId', product);
		}

		if (engagementFile instanceof File) {
			formData.append('engagementLetterFile', engagementFile);
		}
		this.showLoader(true);
		this.createOrderService.createOrder(formData).subscribe({
			next: (res) => {
				this.showLoader(false);
				this.broadcastService.broadcast({
					name: CREATE_MANUAL_ORDER,
					event: {
						data: res.orderId
					}
				});
				setTimeout(() => {
					this.broadcastService.broadcast({
						name: ToasterType.SUCCESS,
						event: {
							message:
								'Create Order Process started: <span class="order-id">' +
								this.createOrderForm.referenceId +
								'</span>'
						}
					});
					this.createOrderForm.referenceId = '';
					this.createOrderForm.engagementFile = null;
					this.resetFileUpload();
				}, 100);
			},
			error: (err) => {
				this.showLoader(false);
				console.error('Order creation failed:', err);
				this.broadcastService.broadcast({
					name: ToasterType.ERROR,
					event: { message: 'Something went wrong. Please try again.' }
				});
			}
		});
	}

	/**
	* Show/Hide the Loader
	* @param isShowing
	*/
	private showLoader(isShowing: boolean): void {
		this.broadcastService.broadcast({
			name: CONTENT_LOADER_EVENT,
			event: {
				isLoader: isShowing,
				message: 'Submitting Order'
			}
		});
	}

	public isGenericPDFSelected(): boolean {
		return this.selectedForm?.form === this.genericPDFForm;
	}

	/**
	 * Loads the organization options.
	 * @param {string} organizationName - Serves to filter api response by organization name.
	 */
	public async loadOrganizationOptions(organizationName?: string) {
		this.organizationList$ = this.createOrderService.getOrganizations(organizationName)
	}

	/**
	 * Handles filter input from the organization dropdown.
	 * Pushes the latest filter value into the subject stream,
	 * which is debounced before triggering the API call.
	 *
	 * @param filterValue - The current input string entered by the user
	 */
	public onOrganizationFilter(filterValue: string) {
		this.organizationFilterSubject.next(filterValue);
	}

	/**
	 * Handles file change events emitted from the engagement file upload component.
	 * Updates the local engagementFile and its validation status.
	 *
	 * @param event - The file change event containing the selected file and its validation result.
	 */
	public onEngagementFileSelect(event: EngagementFileChange): void {
		this.createOrderForm.engagementFile = event.file;
		this.isEngagementFileValid = event.isValid;
	}

	/**
	 * Clears the currently selected engagement file and marks it as valid.
	 * This is typically triggered when the user removes the file manually.
	 */
	public removeEngagementFile(): void {
		this.createOrderForm.engagementFile = null;
		this.isEngagementFileValid = true;
	}

	/**
	 * Resets file-related state and triggers drag-and-drop component reset
	 */
	private resetFileUpload(): void {
		this.createOrderForm.file = null;
		this.fileDropped = false;
		this.mismoFileErrorMessage = null;

		// Force reset drag-and-drop component
		this.displayDragAndDrop = false;
		setTimeout(() => {
			this.displayDragAndDrop = true;
		}, 0);
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next();
		this.atComponentDestroy.complete();
	}
}


